import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Backdrop,
  CircularProgress,
  useTheme,
  alpha,
  Fade,
  Avatar,
} from '@mui/material';
import { 
  Person,
  VideoCall,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import API from '../../../config/API';

const GuestJoin = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { meeting_id, teacher_id } = useParams();
  const { user, dispatch } = useAuthContext();

  const [guestName, setGuestName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meetingInfo, setMeetingInfo] = useState(null);

  // If user is already logged in, redirect to classroom
  useEffect(() => {
    if (user) {
      navigate(`/class_room/${meeting_id}/${teacher_id}`);
    }
  }, [user, meeting_id, teacher_id, navigate]);

  // Fetch meeting info
  useEffect(() => {
    const fetchMeetingInfo = async () => {
      try {
        const response = await API.get(`session/${meeting_id}`);
        if (response.data) {
          setMeetingInfo(response.data);
        }
      } catch (err) {
        console.log('Could not fetch meeting info:', err);
      }
    };
    if (meeting_id) {
      fetchMeetingInfo();
    }
  }, [meeting_id]);

  const handleGuestJoin = async (e) => {
    e.preventDefault();
    
    if (!guestName.trim() || guestName.length < 2) {
      setError('Please enter your name (at least 2 characters)');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Try to register as guest or use anonymous access
      const response = await API.post('auth/guest-join', {
        name: guestName.trim(),
        meeting_id: meeting_id,
      });

      if (response.data?.token) {
        // Save guest user to context
        const guestUser = {
          id: response.data.id || `guest_${Date.now()}`,
          full_name: guestName.trim(),
          token: response.data.token,
          user_type: 0, // Student/attendee
          img_path: null,
          is_guest: true,
        };

        localStorage.setItem('user', JSON.stringify(guestUser));
        dispatch({ type: 'LOGIN', payload: guestUser });
        
        // Navigate to classroom
        navigate(`/class_room/${meeting_id}/${teacher_id}`);
      } else {
        // Fallback: Create local guest session without server registration
        const guestUser = {
          id: `guest_${Date.now()}`,
          full_name: guestName.trim(),
          token: `guest_token_${Date.now()}`,
          user_type: 0,
          img_path: null,
          is_guest: true,
        };

        localStorage.setItem('user', JSON.stringify(guestUser));
        dispatch({ type: 'LOGIN', payload: guestUser });
        
        navigate(`/class_room/${meeting_id}/${teacher_id}`);
      }
    } catch (err) {
      console.log('Guest registration failed, using local guest mode:', err);
      
      // Fallback: Create local guest session
      const guestUser = {
        id: `guest_${Date.now()}`,
        full_name: guestName.trim(),
        token: `guest_token_${Date.now()}`,
        user_type: 0,
        img_path: null,
        is_guest: true,
      };

      localStorage.setItem('user', JSON.stringify(guestUser));
      dispatch({ type: 'LOGIN', payload: guestUser });
      
      navigate(`/class_room/${meeting_id}/${teacher_id}`);
    } finally {
      setIsLoading(false);
    }
  };

  const canJoin = guestName.trim().length >= 2;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: alpha(theme.palette.common.white, 0.1),
          top: -100,
          right: -100,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: alpha(theme.palette.common.white, 0.05),
          bottom: -50,
          left: -50,
        }}
      />

      <Backdrop open={isLoading} sx={{ zIndex: 9999, color: '#fff' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography sx={{ mt: 2 }}>{t('joiningMeeting')}...</Typography>
        </Box>
      </Backdrop>

      <Container maxWidth="sm">
        <Fade in timeout={800}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
              overflow: 'visible',
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
                py: 5,
                textAlign: 'center',
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  margin: '0 auto 16px',
                }}
              >
                <VideoCall sx={{ fontSize: 40 }} />
              </Avatar>
              
              <Typography 
                variant="h4" 
                fontWeight="bold" 
                color="primary"
                sx={{ letterSpacing: '-0.5px' }}
              >
                {t('joinMeeting')}
              </Typography>
              
              {meetingInfo?.title && (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  {meetingInfo.title}
                </Typography>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, opacity: 0.7 }}>
                {t('enterNameToJoin')}
              </Typography>
            </Box>

            <CardContent sx={{ p: 4 }}>
              <Box component="form" onSubmit={handleGuestJoin}>
                {/* Name Field */}
                <Box sx={{ mb: 4 }}>
                  <Typography 
                    variant="body2" 
                    fontWeight={600} 
                    mb={1}
                    color="text.primary"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Person fontSize="small" color="primary" />
                    {t('yourName')}
                  </Typography>
                  <TextField
                    fullWidth
                    value={guestName}
                    onChange={(e) => {
                      setGuestName(e.target.value);
                      setError(null);
                    }}
                    placeholder={t('enterYourName')}
                    error={!!error}
                    helperText={error}
                    autoFocus
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        fontSize: 18,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          }
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
                        }
                      },
                      '& input': {
                        py: 2,
                        textAlign: 'center',
                      }
                    }}
                  />
                </Box>

                {/* Join Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={!canJoin || isLoading}
                  startIcon={<LoginIcon />}
                  sx={{
                    py: 2,
                    fontSize: 18,
                    fontWeight: 600,
                    borderRadius: 3,
                    textTransform: 'none',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.primary.main, 0.5),
                    }
                  }}
                >
                  {t('joinNow')}
                </Button>

                {/* Login Link */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('haveAccount')}{' '}
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => navigate('/')}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      {t('login')}
                    </Button>
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Fade>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="white" sx={{ opacity: 0.8 }}>
            Powered by Perla Tech & Sham Coders
          </Typography>
          <Typography variant="caption" color="white" sx={{ opacity: 0.6, display: 'block', mt: 0.5 }}>
            © {new Date().getFullYear()} Torbet Virtual
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default GuestJoin;
